import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CityStateAutocomplete from "./CityStateAutocomplete";
import { loadGooglePlaces, parseGooglePlace } from "../lib/googlePlaces";

/**
 * H6 (Turno 4, Centro de Llamadas): EL MISMO formulario de cliente que usan ClientesPage.js / ClienteDetailPage.js,
 * extraído aquí para que no haya dos formularios distintos (uno completo en Clientes, otro recortado en la burbuja
 * de la llamada). Incluye Verizon/AT&T según equipo y la dirección con Google Places + catálogo local de
 * ciudad/estado del incidente. Controlado: el padre trae `form`/`setForm` y decide cuándo está `open` (para
 * activar Google Places solo mientras el diálogo esté visible, igual que antes).
 */
export default function ClientFormFields({ form, setForm, open, testIdPrefix = "client", showStatus = false, isGerente = false, isInAttTeam = false, isInVerizonTeam = false }) {
  const [streetInputEl, setStreetInputEl] = useState(null);
  const streetInputRef = useCallback((node) => setStreetInputEl(node), []);
  const autocompleteRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;
  const [placesReady, setPlacesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGooglePlaces().then((ok) => { if (!cancelled) setPlacesReady(ok); });
    return () => { cancelled = true; };
  }, []);

  // Interceptor de CAPTURA a nivel window: un pointerdown dentro del desplegable de Google (.pac-container,
  // insertado en document.body, fuera del árbol de Radix) no debe cerrar el Dialog que lo contiene.
  useEffect(() => {
    const onPointerDownCapture = (e) => {
      if (e.target instanceof Element && e.target.closest(".pac-container")) e.stopPropagation();
    };
    window.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => window.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, []);

  useEffect(() => {
    if (!placesReady || !open || !streetInputEl || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(streetInputEl, { types: ["address"], fields: ["address_components"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place || !place.address_components) return;
      const { street1, city, state, zip } = parseGooglePlace(place);
      setForm({ ...formRef.current, address: street1, city, state, zip });
    });
    autocompleteRef.current = ac;
    return () => {
      if (window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(ac);
      autocompleteRef.current = null;
    };
  }, [placesReady, open, streetInputEl, setForm]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="grid gap-4 py-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm">Nombre *</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-name-input`} />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Email</Label>
          <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-email-input`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm">Teléfono</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-phone-input`} />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Número de Cuenta</Label>
          <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-account-input`} />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm">Dirección (Calle)</Label>
        <Input ref={streetInputRef} value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-address-input`} placeholder="Ej: 123 Main St, Apt 2B" autoComplete="off" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2 col-span-2">
          <Label className="text-sm">Ciudad</Label>
          <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-city-input`} placeholder="Ej: Miami" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Estado (2 letras)</Label>
          <Input value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} maxLength={2} className="bg-secondary border-border" data-testid={`${testIdPrefix}-state-input`} placeholder="FL" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm">ZIP</Label>
        <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} className="bg-secondary border-border" data-testid={`${testIdPrefix}-zip-input`} placeholder="Ej: 33101" />
      </div>
      {/* Ciudad/estado del INCIDENTE (dato del caso de reclamación, no el domicilio del cliente de arriba). */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-sm">Ciudad del Incidente</Label>
          <CityStateAutocomplete
            cityValue={form.incident_city}
            onCityChange={(text) => set("incident_city", text)}
            onSelect={({ city, state }) => setForm({ ...form, incident_city: city, incident_state: state })}
            cityTestId={`${testIdPrefix}-incident-city-input`}
            placeholder="Miami"
            maxLength={100}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Estado del Incidente</Label>
          <Input
            value={form.incident_state}
            onChange={(e) => set("incident_state", e.target.value)}
            placeholder="FL"
            maxLength={100}
            className="bg-secondary border-border"
            data-testid={`${testIdPrefix}-incident-state-input`}
          />
        </div>
      </div>
      {showStatus && (
        <div className="space-y-2">
          <Label className="text-sm">Estado</Label>
          <Select value={form.status} onValueChange={(val) => set("status", val)}>
            <SelectTrigger className="bg-secondary border-border" data-testid={`${testIdPrefix}-status-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="activo">Activo</SelectItem>
              <SelectItem value="inactivo">Inactivo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {(!isGerente && !isInAttTeam && !isInVerizonTeam) && (
        <div className="space-y-2">
          <Label className="text-sm">Compañía *</Label>
          <Select value={form.company} onValueChange={(val) => set("company", val)}>
            <SelectTrigger className="bg-secondary border-border" data-testid={`${testIdPrefix}-company-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="verizon">Verizon</SelectItem>
              <SelectItem value="att">AT&amp;T</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground italic">Define qué plantillas y dominios de envío se usarán para este cliente.</p>
        </div>
      )}
      {isInAttTeam && !isGerente && (
        <div className="space-y-2">
          <Label className="text-sm">Compañía</Label>
          <div className="bg-secondary border border-border rounded px-3 py-2 text-sm" data-testid={`${testIdPrefix}-company-fixed`}>
            <span className="text-blue-300 font-bold">AT&amp;T</span>
            <span className="text-xs text-muted-foreground ml-2">(auto-asignada por tu equipo)</span>
          </div>
        </div>
      )}
      {isInVerizonTeam && !isGerente && (
        <div className="space-y-2">
          <Label className="text-sm">Compañía</Label>
          <div className="bg-secondary border border-border rounded px-3 py-2 text-sm" data-testid={`${testIdPrefix}-company-fixed`}>
            <span className="text-red-300 font-bold">Verizon</span>
            <span className="text-xs text-muted-foreground ml-2">(auto-asignada por tu equipo)</span>
          </div>
        </div>
      )}
    </div>
  );
}
